/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type {
  AccountingDocument,
  InvoiceSortField,
  SortOrder,
  Center,
  DocumentStatus,
} from "@/types/invoices.types";

interface InvoiceTableProps {
  invoices: AccountingDocument[];
  centers?: Center[];
  statuses?: DocumentStatus[];
  onEdit: (invoice: AccountingDocument) => void;
  onDelete: (id: number) => void;
  sortBy?: InvoiceSortField;
  sortOrder?: SortOrder;
  onSort: (field: InvoiceSortField) => void;
}

export function InvoiceTable({
  invoices,
  centers = [],
  statuses = [],
  onEdit,
  onDelete,
  sortBy,
  sortOrder,
  onSort,
}: InvoiceTableProps) {
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<number | null>(null);

  const getCenterName = (id: string) => {
    return centers.find((c) => c.id === id)?.name || id;
  };

  const getStatusName = (code: string) => {
    return statuses.find((s) => s.code === code)?.name || code;
  };

  const handleDeleteClick = (id: number) => {
    setInvoiceToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (invoiceToDelete !== null) {
      setDeletingId(invoiceToDelete);
      onDelete(invoiceToDelete);
      setDeleteDialogOpen(false);
      setInvoiceToDelete(null);
      // Reset deletingId po krátkém timeoutu, aby se stihlo zobrazit disabled stav
      setTimeout(() => setDeletingId(null), 1000);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setInvoiceToDelete(null);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("cs-CZ");
  };

  const formatAmount = (amount?: number, currency?: string) => {
    if (amount === undefined || amount === null) return "-";
    return new Intl.NumberFormat("cs-CZ", {
      style: "currency",
      currency: currency || "CZK",
    }).format(amount);
  };

  const getSortIcon = (field: InvoiceSortField) => {
    if (sortBy !== field) return <ArrowUpDown className="h-4 w-4" />;
    return sortOrder === "ASC" ? (
      <ArrowUp className="h-4 w-4" />
    ) : (
      <ArrowDown className="h-4 w-4" />
    );
  };

  const SortableHeader = ({
    field,
    children,
  }: {
    field: InvoiceSortField;
    children: React.ReactNode;
  }) => (
    <TableHead>
      <Button
        variant="ghost"
        size="sm"
        className="-ml-3 h-8 data-[state=open]:bg-accent"
        onClick={() => onSort(field)}
      >
        {children}
        {getSortIcon(field)}
      </Button>
    </TableHead>
  );

  if (invoices.length === 0) {
    return (
      <div className="bg-white rounded-lg border p-8 text-center text-muted-foreground">
        Žádné faktury nenalezeny
      </div>
    );
  }

  return (
    <>
    <div className="bg-white rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableHeader field="status_code">Status</SortableHeader>
            <SortableHeader field="center_id">Středisko</SortableHeader>
            <TableHead>Číslo faktury</TableHead>
            <SortableHeader field="date_due">Datum splatnosti</SortableHeader>
            <TableHead className="text-right">Částka</TableHead>
            <TableHead className="w-[70px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.map((invoice) => (
            <TableRow
              key={invoice.id}
              onClick={() => onEdit(invoice)}
              className="cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <TableCell>
                <span
                  className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                    invoice.statusCode === "paid"
                      ? "bg-green-50 text-green-700"
                      : invoice.statusCode === "due"
                        ? "bg-yellow-50 text-yellow-700"
                        : "bg-gray-50 text-gray-700"
                  }`}
                >
                  {getStatusName(invoice.statusCode)}
                </span>
              </TableCell>
              <TableCell>{getCenterName(invoice.centerId)}</TableCell>
              <TableCell className="font-medium">
                {invoice.number || "-"}
              </TableCell>
              <TableCell>{formatDate(invoice.dateDue)}</TableCell>
              <TableCell className="text-right">
                {formatAmount(invoice.amountTotal, invoice.currency)}
              </TableCell>
              <TableCell onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      disabled={deletingId === invoice.id}
                    >
                      <span className="sr-only">Otevřít menu</span>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEdit(invoice)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Upravit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={(event) => {
                        event.preventDefault();
                        handleDeleteClick(invoice.id);
                      }}
                      className="text-red-600"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Smazat
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>

    {/* Potvrzovací dialog pro smazání */}
    <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Smazat fakturu</DialogTitle>
          <DialogDescription>
            Opravdu chcete smazat tuto fakturu? Tuto akci nelze vrátit zpět.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={handleDeleteCancel}>
            Zrušit
          </Button>
          <Button variant="destructive" onClick={handleDeleteConfirm}>
            Smazat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
